import React, { useEffect } from "react";
import { useAuth } from "hooks/authHooks";
import { useApolloClient } from "react-apollo-hooks";
import {
  getSubscriptionsOfUserByStatus,
  getSubscriptionsOfUserByStatusVariables,
} from "graphql/types/getSubscriptionsOfUserByStatus";
import {
  GET_CIRCLE_RENEWAL_AGGREGATED,
  GET_SUBSCRIPTIONS_OF_USER_BY_STATUS,
} from "graphql/profiles";
import { UserState, BannerDisplayType } from "graphql/types/globalTypes";
import { SignedinState } from "components/enums/SignedinState";

interface UseCircle {
  isCircleExpired: boolean;
  isUserCircle: boolean;
  renewal: boolean;
  renewalDays: number;
  consultSavings: number;
  pharmaSavings: number;
  diagnosticSavings: number;
  deliverySavings: number;
  totalSavings: number;
  availableHC: number;
  docOnCall: String;
  cards: any[];
}

const useCircle: (props?: any) => UseCircle = (props) => {
  const { setIsLoading, setCircleDetailsLoading } = props || {};
  const [isExpired, setIsExpired] = React.useState<boolean>(false);
  const [isUserCircle, setIsUserCircle] = React.useState<boolean>(false);
  const [renewal, setRenewal] = React.useState<boolean>(false);
  const [renewalDays, setRenewalDays] = React.useState<number>(0);
  const [consultSavings, setConsultSavings] = React.useState<number>(0);
  const [pharmaSavings, setPharmaSavings] = React.useState<number>(0);
  const [diagnosticSavings, setDiagnosticSavings] = React.useState<number>(0);
  const [deliverySavings, setDeliverySavings] = React.useState<number>(0);
  const [totalSavings, setTotalSavings] = React.useState<number>(0);
  const [availableHC, setAvailableHC] = React.useState<number>(0);
  const [docOnCall, setDocOnCall] = React.useState<String>();
  const [cards, setCards] = React.useState<any>([]);
  const { isSignedIn } = useAuth();
  const client = useApolloClient();
  const { currentPatient } = useAuth();

  useEffect(() => {
    !isUserCircle && isSignedIn === SignedinState.SIGNEDIN
      ? client
          .query<
            getSubscriptionsOfUserByStatus,
            getSubscriptionsOfUserByStatusVariables
          >({
            query: GET_SUBSCRIPTIONS_OF_USER_BY_STATUS,
            variables: {
              mobile_number: localStorage.getItem("mobile_num"),
              status: ["active", "disabled", "deferred_active"],
            },
            fetchPolicy: "no-cache",
          })
          .then((response) => {
            const activeSubscriptions: any = {};
            const inactiveSubscriptions: any = {};
            let res = response?.data?.GetSubscriptionsOfUserByStatus?.response;
            res &&
              Object.keys(res).forEach((key) => {
                if (res[key] && res[key].length) {
                  activeSubscriptions[key] = [];
                  inactiveSubscriptions[key] = [];
                  res[key].map((item: any) => {
                    if (
                      item.status === "active" ||
                      item.status === "deferred_active"
                    ) {
                      activeSubscriptions[key].push(item);
                    } else {
                      inactiveSubscriptions[key].push(item);
                    }
                  });
                }
              });
            localStorage.setItem(
              "userSubscriptions",
              JSON.stringify(activeSubscriptions)
            );
            localStorage.setItem(
              "inactiveSubscriptions",
              JSON.stringify(inactiveSubscriptions)
            );
          })
          .catch((error) => {
            console.log(error);
          })
          .finally(() => {
            const userSubscriptions: any = localStorage.getItem(
              "userSubscriptions"
            )
              ? JSON.parse(localStorage.getItem("userSubscriptions"))
              : {};
            const inactiveSubscriptions: any = localStorage.getItem(
              "inactiveSubscriptions"
            )
              ? JSON.parse(localStorage.getItem("inactiveSubscriptions"))
              : {};

            const circleSubs =
              userSubscriptions &&
              userSubscriptions["APOLLO"] &&
              userSubscriptions["APOLLO"].filter(
                (plan: { name: string }) => plan.name === "CIRCLE PLAN"
              );
            setIsUserCircle(circleSubs && circleSubs.length > 0);

            setRenewal(
              circleSubs &&
                circleSubs.length > 0 &&
                circleSubs[0] &&
                circleSubs[0].renewNow
            );
            setRenewalDays(
              circleSubs &&
                circleSubs.length > 0 &&
                circleSubs[0] &&
                circleSubs[0].expires_in
            );
            inactiveSubscriptions &&
              inactiveSubscriptions.APOLLO &&
              inactiveSubscriptions.APOLLO.length > 0 &&
              inactiveSubscriptions.APOLLO.map((item: any) => {
                if (item.status == "disabled") {
                  if (circleSubs && circleSubs.length == 0) {
                    setIsExpired(true);
                    setRenewal(true);
                  }
                } else if (item.status == "deferred_active") {
                  setRenewal(false);
                }
              });
            setCircleDetailsLoading && setCircleDetailsLoading(false);
          })
      : "";
  }, [isSignedIn]);

  useEffect(() => {
    if (
      !localStorage.getItem("mobile_num") &&
      isSignedIn !== SignedinState.SIGNEDIN
    )
      return;
    if (currentPatient?.id) {
      client
        .query({
          query: GET_CIRCLE_RENEWAL_AGGREGATED,
          variables: {
            mobile_number: localStorage.getItem("mobile_num"),
            user_state: UserState.LOGGED_IN,
            banner_context: "HOME",

            patientId: currentPatient && currentPatient.id,
            banner_display_type: [BannerDisplayType.card],
          },
        })
        .then((response: any) => {
          setAvailableHC(response.data?.getOneApolloUser?.availableHC);
          setConsultSavings(
            response.data?.GetCircleSavingsOfUserByMobile?.response?.savings
              ?.consult
          );
          setPharmaSavings(
            response.data?.GetCircleSavingsOfUserByMobile?.response?.savings
              ?.pharma
          );
          setDiagnosticSavings(
            response.data?.GetCircleSavingsOfUserByMobile?.response?.savings
              ?.diagnostics
          );
          setDeliverySavings(
            response.data?.GetCircleSavingsOfUserByMobile?.response?.savings
              ?.delivery
          );
          response.data?.GetCircleSavingsOfUserByMobile?.response?.benefits.map(
            (item: any) => {
              if (item.attribute == "Doc on Call") {
                setDocOnCall(
                  `${item.attribute_type?.remaining}/${item.attribute_type?.total}`
                );
              }
            }
          );
          setTotalSavings(
            response.data?.GetCircleSavingsOfUserByMobile?.response?.savings
              ?.consult +
              response.data?.GetCircleSavingsOfUserByMobile?.response?.savings
                ?.pharma +
              response.data?.GetCircleSavingsOfUserByMobile?.response?.savings
                ?.diagnostics +
              response.data?.GetCircleSavingsOfUserByMobile?.response?.savings
                ?.delivery
          );
          let cardsApi: any[] = [];
          response.data?.GetAllGroupBannersOfUser?.response?.map(
            (item: any) => {
              if (item.banner_display_type == "card") {
                cardsApi.push(item);
              }
            }
          );
          setCards(cardsApi);
        })
        .catch((error: any) => {
          console.error(error);
        })
        .finally(() => {
          setIsLoading && setIsLoading(false);
        });
    }
  }, [isSignedIn]);

  return {
    isCircleExpired: isExpired,
    isUserCircle,
    renewal,
    renewalDays,
    consultSavings,
    pharmaSavings,
    diagnosticSavings,
    deliverySavings,
    totalSavings,
    availableHC,
    docOnCall,
    cards,
  };
};

export default useCircle;
